"use client";

import * as React from "react";
import { useStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  BookMarked,
  Trash2,
  Volume2,
  Search,
  Download,
  ArrowUpDown,
} from "lucide-react";
import { useTextToSpeech } from "@/hooks/use-text-to-speech";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

export function VocabularyList() {
  const vocabulary = useStore((s) => s.vocabulary);
  const removeFromVocabulary = useStore((s) => s.removeFromVocabulary);
  const { speak } = useTextToSpeech();
  const { toast } = useToast();
  const [filter, setFilter] = React.useState("");
  const [sortDesc, setSortDesc] = React.useState(true);

  const filtered = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q
      ? vocabulary.filter(
          (v) =>
            v.word.toLowerCase().includes(q) ||
            (v.translation ?? "").toLowerCase().includes(q)
        )
      : vocabulary;
    return [...list].sort((a, b) =>
      sortDesc ? b.savedAt - a.savedAt : a.savedAt - b.savedAt
    );
  }, [vocabulary, filter, sortDesc]);

  function exportCsv() {
    if (vocabulary.length === 0) return;
    const rows = [
      ["word", "translation", "definition", "context", "savedAt"],
      ...vocabulary.map((v) => [
        v.word,
        v.translation ?? "",
        v.definition ?? "",
        v.context ?? "",
        new Date(v.savedAt).toISOString(),
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `readfluent-vocabulario-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({
      title: "Vocabulário exportado",
      description: `${vocabulary.length} palavras em CSV.`,
    });
  }

  if (vocabulary.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <BookMarked className="h-7 w-7 text-muted-foreground" />
        </div>
        <h3 className="font-medium mb-1">Seu vocabulário está vazio</h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          Quando estiver lendo, selecione uma palavra e pressione <kbd className="px-1.5 py-0.5 text-[10px] rounded border bg-muted">S</kbd> para
          salvá-la aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="shrink-0 p-3 border-b space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Filtrar..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1.5"
            onClick={() => setSortDesc((s) => !s)}
          >
            <ArrowUpDown className="h-3 w-3" />
            {sortDesc ? "Mais recentes" : "Mais antigas"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1.5"
            onClick={exportCsv}
          >
            <Download className="h-3 w-3" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* List */}
      <ScrollArea className="flex-1">
        <div className="divide-y">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Nenhuma palavra corresponde ao filtro.
            </div>
          ) : (
            filtered.map((v) => (
              <div key={v.id} className="p-3 hover:bg-muted/30 transition-colors">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <div className="font-semibold text-base">{v.word}</div>
                    {v.translation && (
                      <div className="text-sm text-muted-foreground">{v.translation}</div>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => speak(v.word)}
                      title="Ouvir pronúncia"
                    >
                      <Volume2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeFromVocabulary(v.id)}
                      title="Remover do vocabulário"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {v.definition && (
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-1.5">
                    {v.definition}
                  </p>
                )}
                {v.context && (
                  <p className="text-xs italic text-muted-foreground/80 line-clamp-1">
                    &ldquo;{v.context}&rdquo;
                  </p>
                )}
                <div className="mt-1.5 flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] font-normal">
                    {new Date(v.savedAt).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "short",
                    })}
                  </Badge>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
